/**
 * `defer-prod-feedback-loop-v011-drawer-cta` v0.1 — wire shape for the
 * "Reproduce as flow" CTA the run-history drawer renders against an
 * agent finding.
 *
 * v0 of this surface (2026-05-29 PO sweep) shipped the
 * `resolveReproduceCtaForFinding` resolver + `buildReproduceCtaPath`
 * helper inside the API. v0.1 (2026-05-29 same-day) exposes the
 * resolver over HTTP so the FE can render the CTA without re-deriving
 * paths from the agent_findings row.
 *
 * The `path` field is built server-side via `buildReproduceCtaPath` so
 * every consumer (flow-builder drawer, landing-page preview, admin
 * findings panel) lands on the canonical URL. We use UUIDs in the
 * path because the `projects` / `test_cases` tables don't carry a
 * slug column today; flipping to slugs is a future migration that
 * does NOT break this contract (consumers treat the segments as
 * opaque router-routed strings).
 *
 * v0.1 explicitly does NOT include:
 *   - The drawer / panel UI itself (still v0.2; needs design partner
 *     sign-off on the panel layout).
 *   - Cross-org sharing via signed URL (v1).
 *   - "Re-draft" if the existing draft is stale (v0.2 — depends on
 *     `pending-prod-feedback-loop` v0.2 schema-evolution worker).
 */

import { z } from 'zod';

export const ReproduceCtaTargetSchema = z.object({
  agentFindingId: z.string().uuid(),
  draftedTestCaseId: z.string().uuid(),
  projectId: z.string().uuid(),
  testCaseId: z.string().uuid(),
  testCaseName: z.string().min(1),
  /**
   * Canonical SPA hash the CTA should navigate to (hash router).
   * Built server-side via `buildReproduceCtaPath` so every consumer
   * lands on the Flow editor — not the project flows list.
   */
  path: z.string().min(1),
});
export type ReproduceCtaTarget = z.infer<typeof ReproduceCtaTargetSchema>;

/**
 * Successful response shape. `null` — i.e. HTTP 404 — when the finding
 * has no draft (worker hasn't run / draft was deleted / cross-org).
 */
export const ReproduceCtaResponseSchema = z.object({
  target: ReproduceCtaTargetSchema,
});
export type ReproduceCtaResponse = z.infer<typeof ReproduceCtaResponseSchema>;
